import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, ArrowLeft, ShieldCheck, CheckCircle2, Calendar, MapPin, Hammer } from 'lucide-react';
import ZoomableImage from '../components/ZoomableImage';
import SEO from '../components/SEO';

const fallbackReferencer = [
  {
    id: '1',
    titel: 'Eksklusiv Villaindkørsel',
    beskrivelse: 'Eksklusiv dansk villaindkørsel udført i natursten med en elegant, fugefri finish. Den våde overflade fremhæver stenenes naturlige farvespil.',
    kategori: 'Indkørsel',
    billede: ['https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/edfc4191-0236-4b5a-915a-2a04051a9ea3.jpg']
  },
  {
    id: '2',
    titel: 'Moderne Terrasse',
    beskrivelse: 'Terrasse med fugefri stenbelægning i lyse natursten. Giver et stilrent og moderne udtryk med ekstremt høj finish, der passer perfekt til havemøblerne.',
    kategori: 'Terrasse',
    billede: ['https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/badd6eea-b45e-42e2-bf84-1f92e8d91be3.jpg']
  },
  {
    id: '3',
    titel: 'Renoveret Trappeforløb',
    beskrivelse: 'Trappe renoveret med helstøbt stenbelægning på både trin og lodrette flader. En slidstærk og skridsikker løsning uden synlige samlinger.',
    kategori: 'Trappe',
    billede: ['https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/aaacfe6e-3946-4690-a3d7-a4f1cfdfe45e.jpg']
  },
  {
    id: '4',
    titel: 'Natursten i Resin (Struktur)',
    beskrivelse: 'Nærbillede af små natursten bundet i en stærk, klar resin. Den faste overflade sikrer en skridsikker tekstur og minimal vedligeholdelse uden løse sten.',
    kategori: 'Detalje',
    billede: ['https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/d9e66450-9d19-4e2f-89b7-8625a6886679.jpg']
  }
];

// Mapping CMS IDs to fallbacks if API fails or for local development
const idMapping = {
  '1665bf81-9603-4aa9-a95f-b82a03b3d2d8': '4',
  '4a8f5d99-c839-4c5e-acef-a7a2883356ca': '3',
  '4d64627f-8dba-436c-8a48-812d7c2188bc': '2',
  '6e122a9a-b052-437e-98bd-a6033e0b0657': '1'
};

export default function ReferenceDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        // Try to fetch from CMS
        const response = await fetch(`https://api.dev.auxo.dk/public/content/referencer/${id}`);
        if (response.ok) {
          const data = await response.json();
          if (data) {
            setProject(data);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.error('Fejl ved hentning af reference-detaljer fra CMS:', err);
      }

      // Fallback logic
      const targetId = idMapping[id] || id;
      const fallbackItem = fallbackReferencer.find(item => item.id === targetId);
      if (fallbackItem) {
        setProject(fallbackItem);
      }
      setLoading(false);
    };

    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <div className="pt-32 pb-20 flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="h-10 w-10 text-emerald-600 animate-spin" />
        <p className="text-stone-500 mt-4 font-medium">Henter projektdetaljer...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="pt-32 pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center min-h-[60vh] flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-stone-900">Projektet blev ikke fundet</h1>
        <p className="text-stone-500 mt-2">Det søgte referenceprojekt findes ikke eller er blevet flyttet.</p>
        <Link
          to="/referencer"
          className="mt-6 inline-flex items-center gap-2 text-emerald-600 font-bold hover:text-emerald-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Tilbage til referencer
        </Link>
      </div>
    );
  }

  const imageUrl = Array.isArray(project.billede) && project.billede[0] 
    ? project.billede[0] 
    : 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/edfc4191-0236-4b5a-915a-2a04051a9ea3.jpg';

  return (
    <div className="pt-24 md:pt-40 xl:pt-28 pb-16">
      <SEO
        title={`${project.titel} | Referencer | Stenbelægning.dk`}
        description={project.beskrivelse ? project.beskrivelse.slice(0, 155) : `Se detaljer og billeder af projektet ${project.titel} udført med helstøbt stenbelægning.`}
        ogImage={imageUrl}
      />
      {/* Navigation & Breadcrumb */}
      <div className="bg-stone-50 py-6 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/referencer"
            className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 font-semibold transition-colors text-sm"
          >
            <ArrowLeft className="h-4 w-4" /> Tilbage til referencer
          </Link>
        </div>
      </div>

      {/* Project Detail Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Column: Image (No border radius) */}
            <div className="lg:col-span-7">
              <div className="bg-stone-100 overflow-hidden shadow-md">
                <ZoomableImage
                  src={imageUrl}
                  alt={project.titel}
                  className="w-full aspect-4/3"
                  zoomScale={2.8}
                  optimizedWidth={1200}
                />
              </div>
            </div>

            {/* Right Column: Project Details */}
            <div className="lg:col-span-5 flex flex-col justify-between space-y-8">
              <div>
                <span className="inline-block bg-stone-900 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider mb-4">
                  {project.kategori}
                </span>
                <h1 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
                  {project.titel}
                </h1>
                <p className="text-stone-600 mt-6 leading-relaxed text-base">
                  {project.beskrivelse}
                </p>
              </div>

              {/* Specifications Grid */}
              <div className="border-t border-b border-stone-100 py-6 space-y-4">
                <h3 className="text-stone-900 font-bold text-sm uppercase tracking-wider">Specifikationer</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2.5 text-stone-600">
                    <Hammer className="h-4 w-4 text-stone-900 shrink-0" />
                    <div>
                      <span className="block text-xs text-stone-400 font-medium">Type</span>
                      <span className="font-semibold text-stone-800">Helstøbt stenbelægning</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 text-stone-600">
                    <ShieldCheck className="h-4 w-4 text-stone-900 shrink-0" />
                    <div>
                      <span className="block text-xs text-stone-400 font-medium">Garanti</span>
                      <span className="font-semibold text-stone-800">10 års garanti</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 text-stone-600">
                    <CheckCircle2 className="h-4 w-4 text-stone-900 shrink-0" />
                    <div>
                      <span className="block text-xs text-stone-400 font-medium">Overflade</span>
                      <span className="font-semibold text-stone-800">Fugefri & skridsikker</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 text-stone-600">
                    <Calendar className="h-4 w-4 text-stone-900 shrink-0" />
                    <div>
                      <span className="block text-xs text-stone-400 font-medium">Vedligeholdelse</span>
                      <span className="font-semibold text-stone-800">Minimal (ingen ukrudt)</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Info Box */}
              <div className="bg-stone-50 p-6 border border-stone-100">
                <h4 className="font-bold text-stone-900 text-sm uppercase tracking-wider mb-2">Hvorfor vælge denne løsning?</h4>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Vores fugefrie stenbelægning kombinerer naturlige sten med en stærk, klar resin. Det giver en fuldstændig fast overflade, hvor ukrudt ikke kan slå rødder, og vand nemt kan dræne igennem.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* CTA Section (Full Width) */}
      <section className="bg-stone-900 py-16 text-white mt-16 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold tracking-tight">
            Ønsker du et lignende projekt?
          </h2>
          <p className="mt-4 text-lg max-w-2xl mx-auto text-stone-300">
            Kontakt os i dag for en uforpligtende snak og et skræddersyet tilbud på din nye, helstøbte stenbelægning.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl text-base font-bold bg-emerald-600 text-white hover:bg-emerald-700 hover:-translate-y-1 transition-all duration-300"
            >
              Få et uforpligtende tilbud
            </Link>
            <Link
              to="/referencer"
              className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl text-base font-bold bg-transparent text-white hover:bg-white/10 border border-white/20 hover:-translate-y-1 transition-all duration-300"
            >
              Se flere referencer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
